import type { HeatmapResult } from "../habits/heatmap";
import type { MarketingSeedApp } from "./types";

/** Real apps from the allowlisted device — used when heatmap seed data is unavailable. */
export const DEFAULT_MARKETING_SEED_APPS: MarketingSeedApp[] = [
  { appIdentifier: "com.ferruccio.screen0", displayName: "Screen0", category: "Productivity", weight: 0.14 },
  { appIdentifier: "com.google.GoogleMobile", displayName: "Chrome", category: "Utilities", weight: 0.22 },
  { appIdentifier: "com.samsung.SmartThings", displayName: "SmartThings", category: "Utilities", weight: 0.06 },
  { appIdentifier: "com.apple.AppStore", displayName: "App Store", category: "Utilities", weight: 0.05 },
  { appIdentifier: "com.apple.Preferences", displayName: "Settings", category: "Utilities", weight: 0.04 },
  { appIdentifier: "com.apple.mobileslideshow", displayName: "Photos", category: "Photo & Video", weight: 0.12 },
  { appIdentifier: "com.apple.mobilemail", displayName: "Mail", category: "Productivity", weight: 0.1 },
  { appIdentifier: "com.apple.MobileSMS", displayName: "Messages", category: "Social Networking", weight: 0.11 },
  { appIdentifier: "com.apple.mobilesafari", displayName: "Safari", category: "Utilities", weight: 0.09 },
  { appIdentifier: "com.spotify.client", displayName: "Spotify", category: "Music", weight: 0.07 },
];

export function extractSeedAppsFromHeatmap(heatmap: HeatmapResult | undefined): MarketingSeedApp[] | null {
  if (!heatmap?.days.length) return null;

  const totals = new Map<string, { app: MarketingSeedApp; seconds: number }>();
  for (const day of heatmap.days) {
    for (const app of day.apps) {
      const key = app.appIdentifier ?? app.displayName;
      const existing = totals.get(key);
      if (existing) {
        existing.seconds += app.durationSeconds;
      } else {
        totals.set(key, {
          app: {
            appIdentifier: app.appIdentifier,
            displayName: app.displayName,
            category: app.category,
            weight: 0,
          },
          seconds: app.durationSeconds,
        });
      }
    }
  }

  const ranked = [...totals.values()]
    .filter((entry) => entry.seconds > 0)
    .sort((a, b) => b.seconds - a.seconds)
    .slice(0, 12);

  if (ranked.length === 0) return null;

  const sum = ranked.reduce((total, entry) => total + entry.seconds, 0);
  return ranked.map((entry) => ({
    ...entry.app,
    weight: entry.seconds / sum,
  }));
}

export function resolveMarketingSeedApps(heatmap: HeatmapResult | undefined): MarketingSeedApp[] {
  return extractSeedAppsFromHeatmap(heatmap) ?? DEFAULT_MARKETING_SEED_APPS;
}
